import { ArrowUpRight } from 'lucide-react';
import { INDIVIDUAL_SERVICES } from '@/data/content';
import { useContent } from '@/lib/content';
import { resolveMediaSrc, thumbSrc } from '@/lib/api';
import { LazyImage } from '@/components/LazyImage';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { scrollToId } from '@/lib/scroll';
import { trackEvent } from '@/lib/analytics';

export const Services = () => {
  const { services, loaded } = useContent();
  return (
  <section id="services" data-testid="services-section" className="py-24 sm:py-32 bg-surface border-y border-white/5">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <SectionHeading chapter="02" label="What We Do" title={<>Celebrations we <span className="italic text-champagne">craft</span></>} />
        <Reveal delay={0.15}>
          <p className="max-w-sm text-white/60 font-light leading-relaxed">
            Every service below is delivered end-to-end by our own team — one point of contact, zero vendor chaos.
          </p>
        </Reveal>
      </div>

      {!loaded && (
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10" data-testid="services-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-[4/5] sm:aspect-[3/4] border-b border-r border-white/10 bg-surface animate-pulse" />
          ))}
        </div>
      )}

      {loaded && (
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
        {services.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 3) * 0.08} className="border-b border-r border-white/10">
            <button
              data-testid={`service-card-${s.slug}`}
              onClick={() => { trackEvent('service_explore', { service: s.slug }); scrollToId('#enquiry'); }}
              className="group relative block w-full text-left overflow-hidden aspect-[4/5] sm:aspect-[3/4]"
            >
              <LazyImage
                src={thumbSrc(s.image)}
                alt={`${s.title} planning and decor by Events by AJ, Hyderabad`}
                wrapperClassName="absolute inset-0"
                className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-[opacity,transform] duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
                <p className="caption-label mb-3">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-serif text-2xl sm:text-3xl font-light text-cream leading-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-white/60 font-light leading-relaxed line-clamp-3">{s.description}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-champagne">
                  Explore Service
                  <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </span>
              </div>
            </button>
          </Reveal>
        ))}
      </div>
      )}

      <Reveal className="mt-12">
        <p className="caption-label mb-5">Also under one roof</p>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {INDIVIDUAL_SERVICES.map((s) => (
            <span key={s} className="text-sm uppercase tracking-[0.2em] text-white/50 flex items-center gap-6">
              {s} <span className="text-champagne/60 text-xs">•</span>
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
  );
};
